import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/pro-regular-svg-icons';
import { Button, useBreakpointValue } from '@chakra-ui/react';
import { IIntakeIdWithDates, IStudentRow } from 'api-paths-cas';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { shallowEqual, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { useSession } from 'shared-components/authentication';
import { IconButtonWithTooltip } from 'shared-components';

import { ApplicationState } from '../../../store';
import { AuthState } from '../../../store/auth/types';
import { getActiveStageTab } from '../../../utilities/helpers';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { parseStudentsToCsvString } from '../../../utilities/exports';
import { getAllStudentForExport } from './getAllStudentForExport';
import { getAuth, getConfig, getIntakeId, getIntakeRange, getUI } from '../../../store/selectors';
import { getStages } from '../../../store/config/selectors';

const getSelected = createSelector(
  (state: ApplicationState) => state,
  ({ dashboard }) => ({
    selectedRows: dashboard.selectedRows,
    allSelected: dashboard.allSelected,
  }),
);

export const ExportAllButton = () => {
  const [exportCsvString, setExportCsvString] = useState('');

  const csvLink = useRef<any>(null);
  const params = new URLSearchParams(window.location.search);

  const { headerTab } = useSelector((state: ApplicationState) => getUI(state), shallowEqual);

  const isPathwayProvider = useSelector(({ auth }: ApplicationState) => auth.isPathway ?? false);

  const totals = useSelector(({ dashboard }: ApplicationState) => dashboard.totals || [], shallowEqual);

  const userRole = useSelector(({ auth }: ApplicationState) => auth.role);
  const { selectedRows, allSelected } = useSelector((state: ApplicationState) => getSelected(state));

  const auth: AuthState = useSelector((state: ApplicationState) => getAuth(state), shallowEqual);
  const stages: string[] = useSelector((state: ApplicationState) => getStages(state), shallowEqual);
  const config = useSelector((state: ApplicationState) => getConfig(state), shallowEqual);
  const intakeId: string = useSelector((state: ApplicationState) => getIntakeId(state));

  const intakeRange = useSelector((state: ApplicationState) => getIntakeRange(state, intakeId!)) as any as
    | IIntakeIdWithDates
    | undefined;

  const sessionContext = useSession();
  const [idToken, setIdToken] = useState('');

  useEffect(() => {
    const getIdToken = async () => {
      try {
        if (sessionContext?.getIdToken && typeof sessionContext.getIdToken === 'function') {
          const idToken = await sessionContext.getIdToken();
          setIdToken(idToken);
        } else {
          console.warn('Failed to export: sessionContext.getIdToken not set:', sessionContext);
        }
      } catch (error) {
        // Handle error gracefully in test environment
        console.warn('Failed to get ID token:', error);
      }
    };
    getIdToken();
  }, [sessionContext, sessionContext?.getIdToken]);

  const handleExport = async () => {
    let exportStudents: IStudentRow[] = await getAllStudentForExport(
      idToken,
      auth,
      config,
      intakeId,
      headerTab,
      intakeRange,
      stages,
      totals,
    );

    if (selectedRows.length > 0 && !allSelected) {
      exportStudents = exportStudents.filter(value => selectedRows.indexOf(value.p_key) > -1);
    }
    const csvString = parseStudentsToCsvString(exportStudents, isPathwayProvider);

    setExportCsvString(csvString);
  };

  useEffect(() => {
    if (exportCsvString) {
      // https://github.com/react-csv/react-csv/issues/221
      setTimeout(() => {
        csvLink?.current?.link.click();
        setExportCsvString('');
      });
    }
  }, [exportCsvString]);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const buttonLabel = selectedRows.length > 0 && !allSelected ? 'Export' : 'Export all';

  return (
    <>
      {hasPerm(userRole, P.STUDENTS_EXPORTS) && (
        <>
          {isMobile ? (
            <IconButtonWithTooltip
              label={buttonLabel}
              iconProps={{ icon: faShare }}
              buttonProps={{ size: 'md', variant: 'ghost' }}
              onClick={handleExport}
            />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              flexShrink="0"
              onClick={handleExport}
              leftIcon={<FontAwesomeIcon className="utility-icon" icon={faShare} />}
            >
              {buttonLabel}
            </Button>
          )}

          <CSVLink
            data={exportCsvString}
            filename={`${getActiveStageTab(params, headerTab)}-${new Date().toDateString().split(' ').join('-')}.csv`}
            target="_blank"
            ref={csvLink}
            style={{ display: 'none' }}
          >
            Download
          </CSVLink>
        </>
      )}
    </>
  );
};
